import { useNavigate } from 'react-router-dom'
import Navigation from './Navigation'
import Footer from './Footer'
import { ChevronRightIcon } from './ui'
import { isDevMode } from '../utils/devMode'
import { usePortalAuth } from '../auth/authContexts'

// Edit-01 icon (pencil/edit) - matches Figma design
function EditIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" 
      />
    </svg>
  )
}

// File-shield-02 icon - using SVG from Figma
function FileShieldIcon() {
  return (
    <img 
      src="/file-shield-02.svg" 
      alt="File shield" 
      className="w-6 h-6"
    />
  )
}

// File-06 icon - using SVG from Figma
function FileIcon() {
  return (
    <img 
      src="/file-06.svg" 
      alt="File" 
      className="w-6 h-6"
    />
  )
}

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  buttonText: string
  onButtonClick?: () => void
}

function FeatureCard({ icon, title, description, buttonText, onButtonClick }: FeatureCardProps) {
  return (
    <div className="bg-background-primary border border-border-secondary border-solid flex flex-col gap-[48px] items-start p-[20px] relative rounded-2xl shadow-xs w-full h-full">
      <div className="bg-background-brand-solid overflow-clip relative rounded-lg shadow-xs shrink-0 size-12">
        <div className="absolute left-[10px] overflow-clip size-6 top-[10px] text-white">
          {icon}
        </div>
              </div>
      <div className="flex flex-col gap-[16px] items-start w-full">
        <div className="flex flex-col gap-[4px] items-start w-full">
          <p className="font-body font-semibold text-lg leading-[28px] text-text-primary-900 w-full">
            {title}
          </p>
          <p className="font-body font-regular text-md leading-md text-text-tertiary-600 w-full">
            {description}
          </p>
        </div>
        <button
          onClick={onButtonClick}
          className="bg-background-brand-solid cursor-pointer flex gap-[6px] items-center justify-center overflow-clip px-[18px] py-[12px] relative rounded-md shadow-xs w-full"
        >
          <span className="font-body font-semibold text-md text-text-white">
            {buttonText}
          </span>
          <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
            <ChevronRightIcon />
          </div>
                  </button>
      </div>
    </div>
  )
}

export default function LandingPage() {
  const { loginWithRedirect } = usePortalAuth()
  const navigate = useNavigate()

  const handleSignIn = async () => {
    // Dev mode: Skip auth and go straight to dashboard
    if (isDevMode()) {
      console.log('🔧 DEV MODE: Skipping auth, redirecting to dashboard')
      navigate('/dashboard')
      return
    }
    try {
      await loginWithRedirect()
    } catch (error) {
      console.error('Error during sign in:', error)
    }
  }

  const handleSignUp = async () => {
    // Dev mode: Skip auth and go straight to dashboard
    if (isDevMode()) {
      console.log('🔧 DEV MODE: Skipping auth, redirecting to dashboard')
      navigate('/dashboard')
      return
    }
    try {
      await loginWithRedirect({ signup: true })
    } catch (error) {
      console.error('Error during sign up:', error)
    }
  }

  return (
    <div className="bg-background-tertiary flex flex-col items-start relative min-h-screen w-full">
      <Navigation />

      <main id="main" className="flex w-full flex-col items-start">
      {/* Page Header - Responsive */}
      <div data-testid="hero-section" className="bg-background-brand-solid flex flex-col gap-[64px] md:gap-0 items-center overflow-clip px-0 py-[40px] md:py-[48px] relative w-full">
        <div className="flex flex-col gap-[48px] md:gap-0 items-center max-w-container-desktop px-[16px] md:px-[32px] py-0 relative w-full">
          <div className="flex flex-col gap-[32px] md:gap-[48px] items-center w-full">
            {/* Heading - Responsive sizing */}
            <div className="flex flex-col gap-[12px] items-start md:items-center text-left md:text-center w-full max-w-[768px]">
              <p className="font-body leading-sm relative shrink-0 text-foreground-brand-primary-alt text-sm md:text-md w-full">
                ONLINE SELF-SERVICE
              </p>
              <h1 className="m-0 font-display leading-[38px] md:leading-[44px] relative shrink-0 text-text-primary-on-brand text-display-sm md:text-display-md w-full tracking-[-0.72px]">
                Manage your policy
              </h1>
            </div>
            
            {/* Actions - Responsive layout */}
            <div className="flex flex-col md:flex-row gap-[12px] items-start md:items-center w-full md:w-auto">
              <button
                onClick={handleSignIn}
                className="bg-background-primary border border-border-primary border-solid flex gap-[6px] items-center justify-center overflow-clip px-[18px] py-[12px] relative rounded-md shadow-xs w-full md:w-auto cursor-pointer"
              >
                <span className="font-body font-semibold text-md text-text-secondary-700">
                  Sign in
                </span>
                              </button>
              <button
                onClick={handleSignUp}
                className="flex gap-[6px] items-center justify-center overflow-clip px-[18px] py-[12px] relative rounded-md w-full md:w-auto cursor-pointer"
              >
                <span className="font-body font-semibold text-md text-text-primary-on-brand">
                  Create an account
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - Responsive grid */}
      <div className="flex flex-col gap-[48px] md:gap-[64px] items-center overflow-clip px-0 py-[48px] md:py-[64px] relative w-full">
        <div className="flex flex-col gap-[32px] md:gap-0 items-center px-[16px] md:px-[32px] py-[16px] relative w-full max-w-container-desktop">
          <div className="flex flex-col md:flex-row gap-[32px] items-stretch relative w-full">
            <FeatureCard
              icon={<EditIcon />}
              title="Update your policy"
              description="Update your registration, drivers and cover options anytime."
              buttonText="Update policy"
            />
            <FeatureCard
              icon={<FileShieldIcon />}
              title="Make a claim anytime"
              description="File a claim online 24/7, and our dedicated claims team will help get you back on the road with minimal hassle."
              buttonText="Start a claim"
            />
            <FeatureCard
              icon={<FileIcon />}
              title="Request documents"
              description="Get a Certificate of Insurance, Tax Invoice, or Payment Receipt sent to your inbox within minutes."
              buttonText="Request documents"
            />
          </div>
        </div>
      </div>
      </main>

      <Footer />
    </div>
  )
}
